import React, { useEffect, useMemo, useState } from 'react';
import './PersonSelectorTransfer.css';

function normalizePeople(allPeople, nameKey, idKey, excludedIdSet) {
    const sourceList = Array.isArray(allPeople)
        ? allPeople
        : Object.values(allPeople || {});

    const deduped = new Map();
    sourceList.forEach((person) => {
        const personId = String(person?.[idKey] ?? '').trim();
        if (!personId || deduped.has(personId) || excludedIdSet.has(personId)) {
            return;
        }

        deduped.set(personId, {
            id: personId,
            name: String(person?.[nameKey] ?? '').trim() || 'Unknown',
            raw: person,
        });
    });

    return Array.from(deduped.values());
}

function idsFromPeople(people, idKey) {
    return (Array.isArray(people) ? people : [])
        .map((person) => String(person?.[idKey] ?? '').trim())
        .filter((idValue) => !!idValue);
}

/**
 * Generic dual-list person selector with multi-select move controls.
 */
export default function PersonSelectorTransfer({
    allPeople = [],
    nameKey = 'displayName',
    idKey = 'cogId',
    selectedPeople = [],
    excludedIds = [],
    onChange = () => {},
    allTitle = 'All people',
    selectedTitle = 'Selected people',
    disabled = false,
    enableLeftSearch = false,
    leftSearchPlaceholder = 'Search',
}) {
    const normalizedExcludedIds = useMemo(
        () => (Array.isArray(excludedIds) ? excludedIds : [])
            .map((value) => String(value || '').trim())
            .filter(Boolean)
            .sort(),
        [excludedIds],
    );

    const excludedIdsKey = useMemo(
        () => normalizedExcludedIds.join('||'),
        [normalizedExcludedIds],
    );

    const excludedIdSet = useMemo(
        () => new Set(excludedIdsKey ? excludedIdsKey.split('||') : []),
        [excludedIdsKey],
    );

    const normalizedPeople = useMemo(
        () => normalizePeople(allPeople, nameKey, idKey, excludedIdSet),
        [allPeople, nameKey, idKey, excludedIdSet],
    );

    const validIdSet = useMemo(
        () => new Set(normalizedPeople.map((person) => person.id)),
        [normalizedPeople],
    );

    const [selectedIds, setSelectedIds] = useState([]);
    const [leftMarkedIds, setLeftMarkedIds] = useState(new Set());
    const [rightMarkedIds, setRightMarkedIds] = useState(new Set());
    const [leftSearchTerm, setLeftSearchTerm] = useState('');

    useEffect(() => {
        const ids = idsFromPeople(selectedPeople, idKey)
            .filter((idValue) => !excludedIdSet.has(idValue))
            .filter((idValue) => validIdSet.has(idValue));
        const uniqueIdSet = new Set(ids);
        const uniqueIds = normalizedPeople
            .map((person) => person.id)
            .filter((personId) => uniqueIdSet.has(personId));

        setSelectedIds((prevIds) => {
            if (
                prevIds.length === uniqueIds.length
                && prevIds.every((idValue, idx) => idValue === uniqueIds[idx])
            ) {
                return prevIds;
            }
            return uniqueIds;
        });
    }, [selectedPeople, idKey, validIdSet, normalizedPeople, excludedIdSet]);

    useEffect(() => {
        setSelectedIds((prevIds) => {
            const filteredIds = prevIds.filter((idValue) => validIdSet.has(idValue));
            if (
                filteredIds.length === prevIds.length
                && filteredIds.every((idValue, idx) => idValue === prevIds[idx])
            ) {
                return prevIds;
            }
            return filteredIds;
        });
    }, [validIdSet]);

    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    const leftItems = useMemo(
        () => normalizedPeople.filter((person) => !selectedIdSet.has(person.id)),
        [normalizedPeople, selectedIdSet],
    );
    const filteredLeftItems = useMemo(() => {
        const normalizedSearch = leftSearchTerm.trim().toLowerCase();
        if (!normalizedSearch) {
            return leftItems;
        }
        return leftItems.filter((person) => person.name.toLowerCase().includes(normalizedSearch));
    }, [leftItems, leftSearchTerm]);
    const rightItems = useMemo(
        () => normalizedPeople.filter((person) => selectedIdSet.has(person.id)),
        [normalizedPeople, selectedIdSet],
    );

    function toggleMarked(setter, currentSet, personId) {
        const nextSet = new Set(currentSet);
        if (nextSet.has(personId)) {
            nextSet.delete(personId);
        } else {
            nextSet.add(personId);
        }
        setter(nextSet);
    }

    function moveRight() {
        if (disabled || leftMarkedIds.size === 0) {
            return;
        }

        const nextSet = new Set(selectedIds);
        leftMarkedIds.forEach((personId) => {
            if (validIdSet.has(personId)) {
                nextSet.add(personId);
            }
        });
        const nextIds = Array.from(nextSet);
        setSelectedIds(nextIds);
        onChange(normalizedPeople.filter((person) => nextSet.has(person.id)).map((person) => person.raw));
        setLeftMarkedIds(new Set());
    }

    function moveLeft() {
        if (disabled || rightMarkedIds.size === 0) {
            return;
        }

        const nextIds = selectedIds.filter((personId) => !rightMarkedIds.has(personId));
        const nextSet = new Set(nextIds);
        setSelectedIds(nextIds);
        onChange(normalizedPeople.filter((person) => nextSet.has(person.id)).map((person) => person.raw));
        setRightMarkedIds(new Set());
    }

    function selectAllRight() {
        if (disabled) {
            return;
        }
        setRightMarkedIds(new Set(rightItems.map((person) => person.id)));
    }

    function deselectAllRight() {
        setRightMarkedIds(new Set());
    }

    return (
        <div className='personSelectorTransfer'>
            <div className='personSelectorColumn'>
                <div className='personSelectorHeading'>{allTitle}</div>
                {enableLeftSearch && (
                    <div className='personSelectorSearchWrap'>
                        <input
                            type='text'
                            className='personSelectorSearchInput'
                            value={leftSearchTerm}
                            onChange={(event) => setLeftSearchTerm(event.target.value)}
                            placeholder={leftSearchPlaceholder}
                            disabled={disabled}
                        />
                    </div>
                )}
                <div className='personSelectorList'>
                    {filteredLeftItems.length === 0 && <div className='personSelectorEmpty'>No people available</div>}
                    {filteredLeftItems.map((person) => (
                        <button
                            key={person.id}
                            type='button'
                            className={leftMarkedIds.has(person.id) ? 'personSelectorRow personSelectorRowMarked' : 'personSelectorRow'}
                            onClick={() => toggleMarked(setLeftMarkedIds, leftMarkedIds, person.id)}
                            disabled={disabled}
                        >
                            <span>{person.name}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className='personSelectorActions'>
                <button type='button' onClick={moveRight} disabled={disabled || leftMarkedIds.size === 0}>
                    {'>'}
                </button>
                <button type='button' onClick={moveLeft} disabled={disabled || rightMarkedIds.size === 0}>
                    {'<'}
                </button>
            </div>

            <div className='personSelectorColumn'>
                <div className='personSelectorHeading'>{selectedTitle}</div>
                <div className='personSelectorToolbar'>
                    <button type='button' onClick={selectAllRight} disabled={disabled || rightItems.length === 0}>
                        Select all
                    </button>
                    <button type='button' onClick={deselectAllRight} disabled={rightMarkedIds.size === 0}>
                        Deselect all
                    </button>
                </div>
                <div className='personSelectorList'>
                    {rightItems.length === 0 && <div className='personSelectorEmpty'>No attendees selected</div>}
                    {rightItems.map((person) => (
                        <button
                            key={person.id}
                            type='button'
                            className={rightMarkedIds.has(person.id) ? 'personSelectorRow personSelectorRowMarked' : 'personSelectorRow'}
                            onClick={() => toggleMarked(setRightMarkedIds, rightMarkedIds, person.id)}
                            disabled={disabled}
                        >
                            <span>{person.name}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
